import React, { useCallback, useEffect, useMemo, useState } from "react";
import { History, Plus, RotateCcw, X } from "lucide-react";
import { api } from "./api";
import { BoutonSombre } from "./ui";
import {
  conditionsAnnulation, duree as dureeTexte, franc, heureFr, heures, horizonTexte, moinsDe, tarifsGroupes,
} from "../lib/reglesPrives";

/* Réglages › Cours privés (Jen, 02.11.2026 : « Pour toutes ces règles de cours privés qu'on a
   définies — le paiement, les heures, quand c'est 50 % ou pas, combien de temps ils peuvent
   annuler — il faudrait que tu mettes tout ça dans les réglages, pour que je puisse repasser au
   travers avec l'école et que s'ils veulent changer, ils peuvent »).
   Façon Réglages de l'iPhone : cinq groupes (Réservation, Disciplines et prix, Annulation et
   absence, Salaire du prof, Factures), une ligne par réglage — son nom, ce qu'il change et pour qui, la
   valeur à droite —, sous chaque groupe ce que lisent les familles avec ces valeurs (aperçu en
   direct), « Enregistrer » et « Revenir aux valeurs de départ ». Le serveur vérifie tout (bornes,
   messages clairs) et note chaque changement au journal (qui, quand, avant → après), affiché en
   bas. Les valeurs de départ sont celles qu'appliquait le code : rien ne change tant que
   personne ne les modifie. Le salaire du prof (Jen, 02.11.2026 au soir) : une ligne par durée
   proposée, le déplacement, « déplacé pour rien » (50 % du salaire) et l'interrupteur du
   déplacement — Cours privés › « À payer aux profs (cash) » › « Modifier » mène ici. */

const egal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/* Le salaire du prof : un changement vaut dès une date (Jen, 02.11.2026 : « on garde la grille
   validée pour le passé — s'ils changent, c'est dès la date du changement, à régler dans les
   réglages »). Les cours d'avant gardent la grille d'avant. */
const SALAIRE = ["prives_salaire_prof", "prives_salaire_deplacement", "prives_salaire_pour_rien_pourcentage",
  "prives_salaire_pour_rien_deplacement"];
const aujourdhui = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Zurich" });
const dateFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");
const QUARTS = [];
for (let m = 5 * 60; m <= 23 * 60 + 30; m += 15) QUARTS.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);

/* Les valeurs de l'écran (`prives_*`) sous les noms courts des phrases (lib/reglesPrives). */
const versRegles = (v) => ({
  delai_heures: Number(v.prives_delai_min_heures), horizon_jours: Number(v.prives_horizon_jours),
  horizon_texte: horizonTexte(v.prives_horizon_jours), pause: Number(v.prives_pause_minutes),
  pas_debut: Number(v.prives_pas_debut), ouverture: v.prives_ouverture, fermeture: v.prives_fermeture,
  annulation_heures: Number(v.prives_annulation_heures), annulation_pourcentage: Number(v.prives_annulation_pourcentage),
  absence_pourcentage: Number(v.prives_absence_pourcentage), absence_pourcentage_max: Number(v.prives_absence_pourcentage_max),
  ecole_facturer_heures: Number(v.prives_ecole_facturer_heures), ecole_facturer_pourcentage: Number(v.prives_ecole_facturer_pourcentage),
  salaires: v.prives_salaire_prof || [], salaire_deplacement: Number(v.prives_salaire_deplacement),
  pour_rien_pourcentage: Number(v.prives_salaire_pour_rien_pourcentage),
  pour_rien_deplacement: v.prives_salaire_pour_rien_deplacement !== false,
  deplacement_minutes: Number(v.prives_deplacement_minutes),
  rappel_premier_jours: Number(v.prives_rappel_premier_jours), rappel_ensuite_jours: Number(v.prives_rappel_ensuite_jours),
});

/* Ce que lisent les familles, l'école et les profs avec ces valeurs (sous chaque groupe). */
const APERCUS = {
  reservation: (R) => `« Au plus tôt ${heures(R.delai_heures)} avant, au plus ${R.horizon_texte} à l'avance. » Débuts proposés toutes les ${R.pas_debut} minutes, de ${heureFr(R.ouverture)} à ${heureFr(R.fermeture)} ; ${R.pause} minutes entre deux cours (une seule salle).`,
  annulation: (R) => {
    const absence = [R.absence_pourcentage, R.absence_pourcentage_max].filter((p, i, t) => t.indexOf(p) === i).map((p) => `${p} %`).join(" ou ");
    return `« ${conditionsAnnulation(R)} » · Absent·e — facturer : ${absence} · L'école annule pour la famille à moins de ${heures(R.ecole_facturer_heures)} : « facturer ${R.ecole_facturer_pourcentage} % ».`;
  },
  prof: (R) => {
    // Jen, 02.11.2026 au soir : un salaire par durée + le déplacement ; déplacé pour rien, 50 %
    // du salaire (+ le déplacement : « vu qu'il s'est déplacé »). Jamais le prix du cours.
    const depl = Number(R.salaire_deplacement) || 0;
    const lignes = R.salaires.filter((x) => Number(x.salaire) > 0);
    const donnes = lignes.map((x) => `${x.minutes} min → ${franc(Number(x.salaire) + depl)}`).join(" · ");
    const ex = lignes[0];
    const pourRien = ex ? (Number(ex.salaire) * R.pour_rien_pourcentage) / 100 + (R.pour_rien_deplacement ? depl : 0) : 0;
    const seuil = R.deplacement_minutes > 0 ? `annulation ${moinsDe(dureeTexte(R.deplacement_minutes))} avant créditée` : "annulation tardive (jamais)";
    return `Cours donné (salaire + ${franc(depl)} de déplacement) : ${donnes}. Déplacé pour rien — famille absente, ${seuil}, annulation de l'école « prof déplacé » : ${R.pour_rien_pourcentage} % du salaire${R.pour_rien_deplacement && depl ? ` + ${franc(depl)}` : ""}${ex ? ` (${ex.minutes} min → ${franc(pourRien)})` : ""}. Annulé plus tôt : rien. Noémie et Miller : pas de salaire.`;
  },
  factures: (R) => `« À relancer » ${R.rappel_premier_jours} jours après l'envoi (« payable dans un délai de ${R.rappel_premier_jours} jours »), puis tous les ${R.rappel_ensuite_jours} jours tant qu'elle n'est pas payée.`,
};

/* Une ligne façon Réglages : le nom et l'aide à gauche, la valeur à droite (dessous sur un
   téléphone étroit). */
const Ligne = ({ libelle, aide, modifie, children, testid }) => (
  <div className="px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-2" data-testid={testid}>
    <div className="min-w-0 flex-1">
      <div className="text-[15px] font-medium leading-snug flex items-center gap-2">
        {libelle}
        {modifie && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#0A84FF" }} title="Modifié, pas encore enregistré" />}
      </div>
      {aide && <p className="text-[12.5px] text-[var(--muted)] leading-snug mt-0.5">{aide}</p>}
    </div>
    <div className="shrink-0 flex items-center gap-2">{children}</div>
  </div>
);

const Nombre = ({ valeur, onChange, champ, testid }) => (
  <>
    <input type="number" inputMode={champ.type === "francs" ? "decimal" : "numeric"} className="input-field text-right tabular-nums"
      style={{ width: 84, paddingTop: 8, paddingBottom: 8 }} min={champ.min} max={champ.max} step={champ.type === "francs" ? "any" : champ.pas || 1}
      value={valeur} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} />
    {champ.unite && <span className="text-[13px] text-[var(--muted)] w-[118px] leading-tight">{champ.unite}</span>}
  </>
);

/* Un interrupteur façon iPhone (vert allumé). */
const Interrupteur = ({ valeur, onChange, testid, libelle }) => (
  <button type="button" role="switch" aria-checked={!!valeur} aria-label={libelle} onClick={() => onChange(!valeur)} data-testid={testid}
    className="relative shrink-0 rounded-full transition-colors" style={{ width: 51, height: 31, background: valeur ? "#34C759" : "#E5E5EA" }}>
    <span className="absolute rounded-full bg-white transition-all" style={{ top: 2, width: 27, height: 27, left: valeur ? 22 : 2, boxShadow: "0 2px 4px rgba(0,0,0,.2)" }} />
  </button>
);

/* Le salaire du prof : une ligne par durée proposée (toutes disciplines confondues), en CHF. */
const GrilleSalaires = ({ champ, lignes, depart, proposees, onChange }) => (
  <div className="px-4 py-3.5 space-y-2" data-testid="prives-ligne-prives_salaire_prof">
    <div>
      <div className="text-[15px] font-medium leading-snug flex items-center gap-2">
        {champ.libelle}
        {!egal(lignes, depart) && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#0A84FF" }} title="Modifié, pas encore enregistré" />}
      </div>
      <p className="text-[12.5px] text-[var(--muted)] leading-snug mt-0.5">{champ.aide}</p>
    </div>
    <div className="rounded-xl border divide-y" style={{ borderColor: "var(--border)" }}>
      {lignes.map((x, i) => (
        <div key={x.minutes} className="flex items-center gap-2 px-3 py-2" data-testid={`prives-salaire-${x.minutes}`}>
          <span className="text-[14px] font-medium tabular-nums">{x.minutes} min</span>
          {!proposees.includes(x.minutes) && <span className="text-[12px] text-[var(--muted)]">· plus proposée aux familles</span>}
          <span className="flex-1" />
          <span className="text-[13px] text-[var(--muted)]">CHF</span>
          <input type="number" inputMode="decimal" className="input-field text-right tabular-nums" style={{ width: 84, paddingTop: 6, paddingBottom: 6 }}
            min={champ.min} max={champ.max} step="any" value={x.salaire} aria-label={`Salaire pour ${x.minutes} min`}
            onChange={(e) => onChange(lignes.map((y, k) => (k === i ? { ...y, salaire: e.target.value === "" ? "" : Number(e.target.value) } : y)))}
            data-testid={`prives-salaire-${x.minutes}-montant`} />
        </div>
      ))}
    </div>
  </div>
);

const Heure = ({ valeur, onChange, testid }) => (
  <select className="input-field tabular-nums" style={{ width: 96, paddingTop: 8, paddingBottom: 8 }}
    value={valeur} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {(QUARTS.includes(valeur) ? QUARTS : [valeur, ...QUARTS]).map((h) => <option key={h} value={h}>{h}</option>)}
  </select>
);

/* Le cadre d'un groupe : titre en petites capitales, la liste blanche aux coins ronds, l'aperçu
   et les deux boutons. */
const Groupe = ({ g, aide, apercu, change, depart, envoi, onEnregistrer, onDepart, onAnnuler, message, children }) => (
  <section className="space-y-2" data-testid={`prives-groupe-${g.id}`}>
    <h3 className="px-4 text-[12.5px] font-semibold uppercase tracking-[0.08em] text-[var(--muted)]">{g.titre}</h3>
    <div className="bg-white rounded-2xl border overflow-hidden divide-y" style={{ borderColor: "var(--border)" }}>
      {children}
    </div>
    <p className="px-4 text-[12.5px] text-[var(--muted)] leading-snug">{aide}</p>
    {apercu && (
      <p className="mx-4 text-[12.5px] leading-snug rounded-xl px-3 py-2" style={{ background: "#F2F2F7", color: "#3A3A3C" }}
        data-testid={`prives-apercu-${g.id}`}>
        <span className="font-semibold">Ce qu'on lit : </span>{apercu}
      </p>
    )}
    {message && (
      <p className="mx-4 text-[13px] rounded-xl px-3 py-2" data-testid={`prives-message-${g.id}`}
        style={message.erreur ? { background: "#FEF2F2", color: "#B91C1C" } : { background: "#F0FDF4", color: "#15803D" }}>
        {message.texte}
      </p>
    )}
    <div className="flex flex-wrap items-center justify-end gap-2 px-1">
      {!depart && (
        <button type="button" className="btn-ghost text-xs inline-flex items-center gap-1" onClick={onDepart} disabled={!!envoi}
          data-testid={`prives-depart-${g.id}`}><RotateCcw size={12} /> Revenir aux valeurs de départ</button>
      )}
      {change && (
        <button type="button" className="btn-ghost text-xs" onClick={onAnnuler} disabled={!!envoi} data-testid={`prives-annuler-${g.id}`}>Annuler</button>
      )}
      <BoutonSombre onClick={onEnregistrer} disabled={!change || !!envoi} testid={`prives-enregistrer-${g.id}`}>
        {envoi === g.id ? "Enregistrement…" : "Enregistrer"}
      </BoutonSombre>
    </div>
  </section>
);

/* Une discipline : qui la donne, ses durées et leur prix, ses deux textes. */
function Discipline({ d, depart, profs, limites, onChange }) {
  const [nouvelle, setNouvelle] = useState(null);
  const maj = (x) => onChange({ ...d, ...x });
  const changerDuree = (i, x) => maj({ durees: d.durees.map((y, k) => (k === i ? { ...y, ...x } : y)) });
  const ajouter = () => {
    const m = Number(nouvelle?.minutes);
    const p = Number(nouvelle?.prix);
    if (!m || !p) return;
    maj({ durees: [...d.durees, { minutes: m, prix: p }].sort((a, b) => a.minutes - b.minutes) });
    setNouvelle(null);
  };
  const basculer = (pid) => maj({ profs: d.profs.includes(pid) ? d.profs.filter((x) => x !== pid) : [...d.profs, pid] });
  return (
    <div className="px-4 py-4 space-y-3" data-testid={`prives-discipline-${d.id}`}>
      <div className="flex items-center gap-2">
        <div className="text-[16px] font-semibold">{d.label}</div>
        {!egal(d, depart) && <span className="w-2 h-2 rounded-full" style={{ background: "#0A84FF" }} title="Modifié, pas encore enregistré" />}
      </div>
      <div>
        <div className="text-[12.5px] text-[var(--muted)] mb-1.5">Qui la donne ({d.profs.length ? `${d.profs.length} prof${d.profs.length > 1 ? "s" : ""}` : "personne : plus proposée sur le site"})</div>
        <div className="flex flex-wrap gap-2" data-testid={`prives-discipline-${d.id}-profs`}>
          {profs.map((p) => {
            const on = d.profs.includes(p.id);
            return (
              <button key={p.id} type="button" onClick={() => basculer(p.id)} aria-pressed={on} data-testid={`prives-discipline-${d.id}-prof-${p.id}`}
                className="rounded-full border px-3 min-h-[36px] text-[13.5px] font-medium transition-colors"
                style={on ? { background: "#0F1B2D", color: "#fff", borderColor: "#0F1B2D" } : { background: "#fff", color: "var(--muted)", borderColor: "var(--border)" }}>
                {on ? "✓ " : ""}{p.prenom}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-[12.5px] text-[var(--muted)] mb-1.5">Durées et prix</div>
        <div className="rounded-xl border divide-y" style={{ borderColor: "var(--border)" }}>
          {d.durees.map((x, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`prives-duree-${d.id}-${x.minutes}`}>
              <input type="number" inputMode="numeric" className="input-field text-right tabular-nums" style={{ width: 72, paddingTop: 6, paddingBottom: 6 }}
                min={limites.minutes[0]} max={limites.minutes[1]} step={5} value={x.minutes}
                onChange={(e) => changerDuree(i, { minutes: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`prives-duree-${d.id}-${i}-minutes`} />
              <span className="text-[13px] text-[var(--muted)]">min</span>
              <span className="flex-1" />
              <span className="text-[13px] text-[var(--muted)]">CHF</span>
              <input type="number" inputMode="decimal" className="input-field text-right tabular-nums" style={{ width: 84, paddingTop: 6, paddingBottom: 6 }}
                min={limites.prix[0]} max={limites.prix[1]} step="any" value={x.prix}
                onChange={(e) => changerDuree(i, { prix: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`prives-duree-${d.id}-${i}-prix`} />
              <button type="button" aria-label={`Retirer ${x.minutes} min`} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[#F1F5F9] disabled:opacity-30"
                disabled={d.durees.length <= 1} onClick={() => maj({ durees: d.durees.filter((_, k) => k !== i) })}
                data-testid={`prives-duree-${d.id}-${i}-retirer`}><X size={14} /></button>
            </div>
          ))}
          {nouvelle ? (
            <div className="flex items-center gap-2 px-3 py-2" data-testid={`prives-duree-${d.id}-nouvelle`}>
              <input type="number" inputMode="numeric" placeholder="45" className="input-field text-right" style={{ width: 72, paddingTop: 6, paddingBottom: 6 }}
                value={nouvelle.minutes} onChange={(e) => setNouvelle({ ...nouvelle, minutes: e.target.value })} data-testid={`prives-duree-${d.id}-nouvelle-minutes`} />
              <span className="text-[13px] text-[var(--muted)]">min</span>
              <span className="flex-1" />
              <span className="text-[13px] text-[var(--muted)]">CHF</span>
              <input type="number" inputMode="decimal" placeholder="75" className="input-field text-right" style={{ width: 84, paddingTop: 6, paddingBottom: 6 }}
                value={nouvelle.prix} onChange={(e) => setNouvelle({ ...nouvelle, prix: e.target.value })} data-testid={`prives-duree-${d.id}-nouvelle-prix`} />
              <button type="button" className="btn-ghost text-xs" onClick={ajouter} data-testid={`prives-duree-${d.id}-nouvelle-ok`}>Ajouter</button>
            </div>
          ) : (
            <button type="button" className="w-full flex items-center gap-1.5 px-3 py-2.5 text-[13.5px] font-medium" style={{ color: "#0A84FF" }}
              onClick={() => setNouvelle({ minutes: "", prix: "" })} data-testid={`prives-duree-${d.id}-ajouter`}><Plus size={14} /> Ajouter une durée</button>
          )}
        </div>
      </div>
      <label className="block">
        <span className="text-[12.5px] text-[var(--muted)]">Description (page de réservation)</span>
        <textarea className="input-field w-full mt-1 text-[14px]" rows={3} maxLength={limites.description} value={d.description}
          onChange={(e) => maj({ description: e.target.value })} data-testid={`prives-discipline-${d.id}-description`} />
      </label>
      <label className="block">
        <span className="text-[12.5px] text-[var(--muted)]">À prévoir (confirmation, e-mail, agenda)</span>
        <textarea className="input-field w-full mt-1 text-[14px]" rows={2} maxLength={limites.a_prevoir} value={d.a_prevoir}
          onChange={(e) => maj({ a_prevoir: e.target.value })} data-testid={`prives-discipline-${d.id}-a-prevoir`} />
      </label>
    </div>
  );
}

const quandJournal = (iso) => {
  const d = new Date(iso || "");
  return Number.isNaN(d.getTime()) ? "" : `${d.toLocaleDateString("fr-CH")} à ${d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}`;
};

export default function ReglagesPrives({ demander }) {
  const [d, setD] = useState(null);
  const [v, setV] = useState(null);
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState("");
  const [messages, setMessages] = useState({});
  const [des, setDes] = useState("");
  const charger = useCallback(async () => {
    const x = await api("/reglages/prives");
    setD(x); setV(x.valeurs);
    return x;
  }, []);
  useEffect(() => { charger().catch((e) => setErreur(e.message)); }, [charger]);
  const R = useMemo(() => (v ? versRegles(v) : null), [v]);
  if (erreur && !d) return <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>;
  if (!d || !v) return <p className="text-sm text-[var(--muted)]">Chargement des réglages des cours privés…</p>;

  const champ = (cle) => d.champs[cle];
  const change = (g) => g.cles.some((k) => !egal(v[k], d.valeurs[k]));
  const auDepart = (g) => g.cles.every((k) => k === "prives_salaire_des" || egal(d.valeurs[k], d.depart[k]));
  const salaireChange = SALAIRE.some((k) => !egal(v[k], d.valeurs[k]));
  // Avec la grille du prof : la date dès laquelle elle vaut (aujourd'hui d'office).
  const avecDate = (g, valeurs) => (g.id === "prof" && SALAIRE.some((k) => k in valeurs && !egal(valeurs[k], d.valeurs[k]))
    ? { ...valeurs, prives_salaire_des: des || aujourdhui() } : valeurs);
  const enregistrer = async (g, valeurs, texte) => {
    setEnvoi(g.id); setMessages((m) => ({ ...m, [g.id]: null }));
    try {
      await api("/reglages", { method: "PATCH", body: JSON.stringify({ valeurs: avecDate(g, valeurs) }) });
      if (g.id === "prof") setDes("");
      const x = await charger();
      setV((avant) => ({ ...avant, ...Object.fromEntries(g.cles.map((k) => [k, x.valeurs[k]])) }));
      setMessages((m) => ({ ...m, [g.id]: { texte } }));
    } catch (e) {
      setMessages((m) => ({ ...m, [g.id]: { texte: e.message, erreur: true } }));
    }
    setEnvoi("");
  };
  const enregistrerGroupe = (g) => enregistrer(g, Object.fromEntries(g.cles.filter((k) => k !== "prives_salaire_des" && !egal(v[k], d.valeurs[k])).map((k) => [k, v[k]])),
    `${g.titre} : enregistré. Le site, les e-mails, les factures et les apps suivent tout de suite.`);
  const revenir = async (g) => {
    const lisible = (k, x) => {
      const c = champ(k);
      if (k === "prives_disciplines") return "Disciplines : durées, prix, textes et profs de départ";
      if (k === "prives_salaire_prof") return `${c.libelle} : ${x.map((y) => `${y.minutes} min ${franc(y.salaire)}`).join(", ")}`;
      if (c.type === "interrupteur") return `${c.libelle} : ${x ? "oui" : "non"}`;
      if (c.type === "francs") return `${c.libelle} : ${franc(x)} par cours`;
      return `${c.libelle} : ${x} ${c.unite || ""}`.trim();
    };
    const lignes = g.cles.filter((k) => k !== "prives_salaire_des" && !egal(d.valeurs[k], d.depart[k])).map((k) => lisible(k, d.depart[k]));
    if (g.id === "prof" && SALAIRE.some((k) => !egal(d.valeurs[k], d.depart[k]))) lignes.push(`Dès le ${dateFr(des || aujourdhui())} ; les cours d'avant gardent leur grille`);
    if (!await demander({
      titre: `Revenir aux valeurs de départ : ${g.titre} ?`,
      texte: "Les valeurs qu'appliquait la console avant que l'école ne les change. Les cours déjà réservés gardent leur prix.",
      points: lignes,
      valider: "Revenir aux valeurs de départ",
      annuler: "Garder",
    })) return;
    enregistrer(g, Object.fromEntries(g.cles.filter((k) => k !== "prives_salaire_des").map((k) => [k, d.depart[k]])), `${g.titre} : revenu aux valeurs de départ.`);
  };
  const annuler = (g) => { setV({ ...v, ...Object.fromEntries(g.cles.map((k) => [k, d.valeurs[k]])) }); if (g.id === "prof") setDes(""); };
  const ligneNombre = (k) => (
    <Ligne key={k} libelle={champ(k).libelle} aide={champ(k).aide} modifie={!egal(v[k], d.valeurs[k])} testid={`prives-ligne-${k}`}>
      <Nombre champ={champ(k)} valeur={v[k]} onChange={(x) => setV({ ...v, [k]: x })} testid={`prives-champ-${k}`} />
    </Ligne>
  );
  const groupe = (id) => d.groupes.find((g) => g.id === id);
  const cadre = (id, children) => {
    const g = groupe(id);
    return (
      <Groupe key={id} g={g} aide={g.aide} apercu={APERCUS[id]?.(R)} change={change(g)} depart={auDepart(g)} envoi={envoi}
        onEnregistrer={() => enregistrerGroupe(g)} onDepart={() => revenir(g)} onAnnuler={() => annuler(g)} message={messages[id]}>
        {children}
      </Groupe>
    );
  };
  const tarifs = tarifsGroupes(v.prives_disciplines.map((x) => ({ ...x, durees: x.durees.filter((y) => Number(y.minutes) > 0 && Number(y.prix) > 0) })));

  return (
    <div className="max-w-3xl space-y-8" data-testid="reglages-prives">
      <p className="text-[13.5px] text-[var(--muted)] leading-snug px-1">
        Les règles des cours privés, telles que les lisent les familles (site, e-mails, factures, app famille), les profs
        (leur app) et l'école (console, app école). Chaque changement s'applique tout de suite et se note dans le journal,
        en bas. Le prix d'un cours déjà réservé ne change jamais.
      </p>

      {cadre("reservation", (
        <>
          {ligneNombre("prives_delai_min_heures")}
          {ligneNombre("prives_horizon_jours")}
          {ligneNombre("prives_pas_debut")}
          <Ligne libelle="Heures d'ouverture" aide={champ("prives_ouverture").aide}
            modifie={v.prives_ouverture !== d.valeurs.prives_ouverture || v.prives_fermeture !== d.valeurs.prives_fermeture} testid="prives-ligne-ouverture">
            <span className="text-[13px] text-[var(--muted)]">de</span>
            <Heure valeur={v.prives_ouverture} onChange={(x) => setV({ ...v, prives_ouverture: x })} testid="prives-champ-prives_ouverture" />
            <span className="text-[13px] text-[var(--muted)]">à</span>
            <Heure valeur={v.prives_fermeture} onChange={(x) => setV({ ...v, prives_fermeture: x })} testid="prives-champ-prives_fermeture" />
          </Ligne>
          {ligneNombre("prives_pause_minutes")}
          {ligneNombre("prives_limite_par_heure")}
        </>
      ))}

      {cadre("disciplines", (
        <>
          {v.prives_disciplines.map((x, i) => (
            <Discipline key={x.id} d={x} depart={d.valeurs.prives_disciplines[i]} profs={d.profs} limites={d.limites}
              onChange={(y) => setV({ ...v, prives_disciplines: v.prives_disciplines.map((z, k) => (k === i ? y : z)) })} />
          ))}
          <div className="px-4 py-3 text-[12.5px] text-[var(--muted)]" data-testid="prives-tarifs-apercu">
            <span className="font-semibold text-[var(--text)]">Page Tarifs : </span>
            {tarifs.flatMap((g) => g.durees.map((y) => `${g.labels.join(" / ")} · ${dureeTexte(y.minutes)} — CHF ${franc(y.prix)}`)).join(" · ")}
            {!egal(v.prives_disciplines, d.depart.prives_disciplines) && (
              <span className="block mt-1">
                Au départ : {d.depart.prives_disciplines.map((y) => `${y.label} ${y.durees.map((z) => `${z.minutes} min ${franc(z.prix)}`).join(", ")}`).join(" · ")}.
              </span>
            )}
          </div>
        </>
      ))}

      {cadre("annulation", (
        <>
          {ligneNombre("prives_annulation_heures")}
          {ligneNombre("prives_annulation_pourcentage")}
          {ligneNombre("prives_absence_pourcentage")}
          {ligneNombre("prives_absence_pourcentage_max")}
          {ligneNombre("prives_ecole_facturer_heures")}
          {ligneNombre("prives_ecole_facturer_pourcentage")}
        </>
      ))}

      {cadre("prof", (
        <>
          <GrilleSalaires champ={champ("prives_salaire_prof")} lignes={v.prives_salaire_prof} depart={d.valeurs.prives_salaire_prof}
            proposees={d.durees_proposees || []} onChange={(x) => setV({ ...v, prives_salaire_prof: x })} />
          {ligneNombre("prives_salaire_deplacement")}
          {ligneNombre("prives_salaire_pour_rien_pourcentage")}
          <Ligne libelle={champ("prives_salaire_pour_rien_deplacement").libelle} aide={champ("prives_salaire_pour_rien_deplacement").aide}
            modifie={v.prives_salaire_pour_rien_deplacement !== d.valeurs.prives_salaire_pour_rien_deplacement}
            testid="prives-ligne-prives_salaire_pour_rien_deplacement">
            <Interrupteur valeur={v.prives_salaire_pour_rien_deplacement} libelle={champ("prives_salaire_pour_rien_deplacement").libelle}
              onChange={(x) => setV({ ...v, prives_salaire_pour_rien_deplacement: x })} testid="prives-champ-prives_salaire_pour_rien_deplacement" />
          </Ligne>
          <Ligne libelle={champ("prives_salaire_des").libelle} aide={champ("prives_salaire_des").aide} modifie={salaireChange}
            testid="prives-ligne-prives_salaire_des">
            {salaireChange ? (
              <input type="date" className="input-field text-[14px] py-1.5" required min={d.valeurs.prives_salaire_des || undefined}
                value={des || aujourdhui()} onChange={(e) => setDes(e.target.value)} aria-label="Nouvelle grille dès le"
                data-testid="prives-champ-prives_salaire_des" />
            ) : (
              <span className="text-[14px] tabular-nums" data-testid="prives-salaire-des-texte">
                {d.valeurs.prives_salaire_des ? `le ${dateFr(d.valeurs.prives_salaire_des)}` : "depuis toujours"}
              </span>
            )}
          </Ligne>
          {(d.salaire_historique || []).length > 0 && (
            <div className="px-4 py-3 text-[12.5px] text-[var(--muted)] space-y-1" data-testid="prives-salaire-historique">
              <div className="font-semibold text-[var(--text)]">Les cours d'avant gardent leur grille</div>
              {d.salaire_historique.map((h) => (
                <div key={h.jusqu_au}>
                  {h.des ? `Du ${dateFr(h.des)} au` : "Avant le"} {h.des ? dateFr(new Date(Date.parse(`${h.jusqu_au}T12:00:00Z`) - 864e5).toISOString()) : dateFr(h.jusqu_au)} : {h.texte}
                </div>
              ))}
            </div>
          )}
          {ligneNombre("prives_deplacement_minutes")}
        </>
      ))}

      {cadre("factures", (
        <>
          {ligneNombre("prives_rappel_premier_jours")}
          {ligneNombre("prives_rappel_ensuite_jours")}
        </>
      ))}

      <section className="space-y-2" data-testid="prives-journal">
        <h3 className="px-4 text-[12.5px] font-semibold uppercase tracking-[0.08em] text-[var(--muted)] inline-flex items-center gap-1.5">
          <History size={13} /> Journal des changements
        </h3>
        <div className="bg-white rounded-2xl border overflow-hidden divide-y" style={{ borderColor: "var(--border)" }}>
          {!d.journal.length ? (
            <p className="px-4 py-3.5 text-[13.5px] text-[var(--muted)]">Aucun changement : toutes les règles ont leur valeur de départ.</p>
          ) : d.journal.map((j, k) => (
            <div key={k} className="px-4 py-3" data-testid="prives-journal-ligne">
              <div className="text-[12px] text-[var(--muted)]">{quandJournal(j.le)}{j.par ? ` · ${j.par}` : ""} · {j.groupe}</div>
              <div className="text-[14px] font-medium leading-snug">{j.libelle}</div>
              <div className="text-[13px] leading-snug">{j.changements.join(" · ")}</div>
            </div>
          ))}
        </div>
        <p className="px-4 text-[12px] text-[var(--muted)]">Tous les changements de la console sont aussi dans Journal &amp; comptes.</p>
      </section>
    </div>
  );
}
